import React, { useMemo } from 'react';
import {
    CampaignManagerBooking,
    CampaignManagerBookingTotals,
    CampaignManagerInfluencer,
    PatchCampaignManagerBookingData,
} from '@round/api';
import useNonNullContext from '../../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../../contexts/OptionsContext';
import { Column, ColumnInstance } from 'react-table';
import cn from 'classnames';
import styles from '../../../booking/TableCells/BookingsTable.module.css';
import { asMoney, formatDate, formatYesNo } from '../../../../utils/utits';
import StatusPicker from '../../../booking/TableCells/StatusPicker/StatusPicker';
import { PaymentTermsLabels } from '../../../../utils/constants';
import Table from '../../../../ui/Table/Table';
import InfoCell from '../../../booking/TableCells/InfoCell';
import CampaignTypeCell from '../../../booking/TableCells/CampaignTypeCell';
import BookingPriceCell from '../../../booking/TableCells/BookingPriceCell';
import PostLinkCell from '../../../booking/TableCells/PostLinkCell';
import BoostCodeCell from '../../../booking/TableCells/BoostCodeCell';
import FinancialReferenceCell from '../../../booking/TableCells/FinancialReferenceCell';
import ApproveToBillCell from '../../../booking/TableCells/ApproveToBillCell';
import { renderTotalsAsMoney } from '../../../booking/TableCells/helpers';
import Skeleton from '../../../../ui/Skeleton/Skeleton';
import range from 'lodash/range';
import CreatorCell from '../../../booking/TableCells/CreatorCell';
import ReasonsToBlockApprovalCell from '../../../booking/TableCells/ReasonsToBlockApprovalCell';
import PostTypeCell from '../../../booking/TableCells/PostTypeCell';
import IGStoryUploadCell from '../../../booking/TableCells/IGStoryUpload/IGStoryUploadCell';

export type CampaignManagerBookingsTableRow = CampaignManagerBooking & {
    influencer?: CampaignManagerInfluencer;
};

type CampaignManagerBookingsTableProps = {
    className?: string;
    data: CampaignManagerBooking[];
    totals: CampaignManagerBookingTotals | null;
    loading?: boolean;
    updateBooking?: (bookingId: number, data: PatchCampaignManagerBookingData) => Promise<void>;
    onStatusChange?: (bookingId: number, status: CampaignManagerBooking['status']) => Promise<void>;
    approveToBill?: (booking: CampaignManagerBookingsTableRow) => void;
};

const CampaignManagerBookingsTable = ({
    className,
    data,
    loading,
    updateBooking,
    onStatusChange,
    approveToBill,
    totals,
}: CampaignManagerBookingsTableProps) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const columns = useMemo(() => {
        const columns: (Column<CampaignManagerBookingsTableRow> & { editable?: boolean })[] = [
            {
                Header: 'ID',
                accessor: 'id',
            },
            {
                Header: 'Creator',
                accessor: 'influencer_name',
                Cell: CreatorCell,
            },
            {
                Header: 'Post Type',
                accessor: 'post_type',
                Cell: PostTypeCell,
            },
            {
                Header: 'Type',
                accessor: 'campaign_type',
                Cell: CampaignTypeCell,
            },
            {
                Header: 'Manager',
                accessor: 'manager_email',
                Cell: InfoCell,
            },
            {
                Header: 'Client',
                accessor: 'client_name',
                Cell: InfoCell,
            },
            {
                Header: 'Booker',
                accessor: 'booker_email',
                Cell: InfoCell,
            },
            {
                Header: 'Campaign Name',
                accessor: 'campaign_name',
                Cell: (props) => <InfoCell {...props} className={styles.campaignName} />,
            },
            {
                Header: 'Date Created',
                accessor: 'created',
                Cell: ({ value }) => <>{formatDate(value, { month: 'numeric' })}</>,
            },
            {
                Header: 'Currency',
                accessor: 'currency',
                Cell: ({ value }) => <>{currencies.find((currency) => currency.id === value)?.name}</>,
            },
            {
                Header: 'Booking Price',
                accessor: 'price',
                editable: true,
                Cell: (props) => <BookingPriceCell {...props} updateBooking={updateBooking} />,
            },
            {
                Header: 'Creator Payment Amount',
                accessor: 'influencer_payment_amount',
                Cell: ({
                    value,
                    row: {
                        original: { currency },
                    },
                }) => (
                    <>
                        {asMoney(
                            value,
                            currencies.find((c) => c.id === currency)
                        )}
                    </>
                ),
            },
            {
                Header: 'Status',
                accessor: 'status',
                editable: true,
                Cell: ({
                    value,
                    row: {
                        original: { id },
                    },
                }) => (
                    <StatusPicker
                        status={value}
                        onChange={(status) => {
                            if (typeof onStatusChange === 'function') {
                                onStatusChange(id, status);
                            }
                        }}
                    />
                ),
            },
            {
                Header: 'Post Link',
                accessor: 'post_url',
                editable: true,
                Cell: (props) =>
                    props.row.original.post_type === 'instagram_story' ? (
                        <IGStoryUploadCell updateBooking={updateBooking} {...props} />
                    ) : (
                        <PostLinkCell {...props} updateBooking={updateBooking} />
                    ),
            },
            {
                Header: 'Boost Code',
                accessor: 'tiktok_ad_code',
                editable: true,
                Cell: (props) => <BoostCodeCell {...props} updateBooking={updateBooking} />,
            },
            {
                Header: 'PO/Financial Reference',
                accessor: 'finance_reference',
                editable: true,
                Cell: (props) => <FinancialReferenceCell {...props} updateBooking={updateBooking} />,
            },
            {
                Header: 'Reasons To Block Approval',
                accessor: 'reasons_to_block_approval_to_bill',
                Cell: ReasonsToBlockApprovalCell,
            },
            {
                Header: 'Approved To Bill?',
                accessor: 'date_approved',
                id: 'approvedToBill',
                editable: true,
                Cell: (props) => <ApproveToBillCell {...props} approveToBill={approveToBill} />,
            },
            {
                Header: 'Date Approved',
                accessor: 'date_approved',
                Cell: ({ value }) => <>{typeof value === 'string' ? formatDate(value) : ''}</>,
            },
            {
                Header: 'Invoice Paid?',
                accessor: 'is_invoice_paid',
                Cell: ({ value }) => <>{formatYesNo(value)}</>,
            },
            {
                Header: 'Creator Paid?',
                id: 'creator_paid',
                accessor: 'date_influencer_paid',
                Cell: ({ value }) => <>{formatYesNo(Boolean(value))}</>,
            },
            {
                Header: 'Creator Payment Date',
                accessor: 'date_influencer_paid',
                Cell: ({ value }) => <>{typeof value === 'string' ? formatDate(value) : ''}</>,
            },
            {
                Header: 'Creator Payment Terms',
                accessor: 'payment_terms',
                Cell: ({ value }) => <>{PaymentTermsLabels[value]?.label}</>,
            },
            {
                Header: 'Manager Paid?',
                accessor: 'is_manager_paid',
                Cell: ({ value }) => <>{formatYesNo(value)}</>,
            },
        ];

        return columns;
    }, [updateBooking, onStatusChange, approveToBill, currencies]);

    return (
        <Table
            className={cn(styles.table, className)}
            columns={columns as Column<CampaignManagerBooking>[]}
            data={data}
            renderRow={(row, onClick) => (
                <tr
                    {...row.getRowProps()}
                    onClick={(e) => {
                        if (typeof onClick === 'function') {
                            onClick(e, row);
                        }
                    }}
                >
                    {row.cells.map((cell) => (
                        <td
                            {...cell.getCellProps()}
                            className={cn({
                                [styles.editable]: (
                                    cell.column as ColumnInstance<CampaignManagerBooking> & {
                                        editable?: boolean;
                                    }
                                ).editable,
                            })}
                        >
                            {cell.render('Cell')}
                        </td>
                    ))}
                </tr>
            )}
            renderBody={({ rows, prepareRow, columns }, renderRow) => (
                <tbody>
                    {rows.map((row) => {
                        prepareRow(row);
                        return renderRow(row);
                    })}
                    {loading &&
                        range(0, 10).map((i) => (
                            <tr key={i}>
                                {columns.map((column) => (
                                    <td key={column.id}>
                                        <Skeleton style={{ zIndex: 'unset' }} />
                                    </td>
                                ))}
                            </tr>
                        ))}
                </tbody>
            )}
            renderFooter={() => (
                <tfoot>
                    <tr>
                        <td />
                        <td />
                        <td colSpan={8} />
                        <td>{totals ? renderTotalsAsMoney(totals.price, currencies) : '-'}</td>
                        <td colSpan={13} />
                    </tr>
                </tfoot>
            )}
        />
    );
};

export default CampaignManagerBookingsTable;
